import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { CLIP, QUALIFIERS } from '../../enums';
import { ClipFavourite, getClipFavourites, setClipFavourites } from '../../api/clipFavouritesApi';
import { useAppSelector } from '../../store/hooks';

type FavouriteRow = { id: string; name: string; details?: { color?: string }; clips: (CLIP & { qualifiers?: QUALIFIERS[] })[] };
export type SidebarList = 'selected' | 'favourites';
export type ClipSort = 'chronological' | 'row' | 'added';
export type SidebarClip = CLIP & { rowIndex: number; clipIndex: number; topicId: string; topicName: string; qualifiers?: QUALIFIERS[] };
export type FavouriteChange = { clipIds: string[]; favourite: boolean; previous: ClipFavourite[] };
type EditorActions = {
  pause: () => void;
  select: (clip: CLIP, additive: boolean) => void;
  deselect: (clips: CLIP | CLIP[]) => void;
  edit: (clip: CLIP) => void;
  setAngleLock: (clip: CLIP, angleId: number | null) => Promise<void>;
  canLockAngle: (clip: CLIP) => boolean;
  isPlayheadOverClip: (clip: CLIP) => boolean;
  seek: (clip: CLIP, playing: boolean, seconds?: number) => void;
  send: (clips: CLIP[]) => void;
  onFavouritesChange?: (change: FavouriteChange) => void;
};
type EditorData = { rows: FavouriteRow[]; canEdit: boolean; canSend: boolean; activeClipId?: string };
type StarFeedback = { clipIds: Set<string>; favourite: boolean; key: number };
const emptyEditor: EditorData = { rows: [], canEdit: false, canSend: false };

type FavouritesContext = {
  feedId: string;
  ids: Set<string>;
  hideSent: boolean;
  setHideSent: React.Dispatch<React.SetStateAction<boolean>>;
  sentCount: number;
  pending: Set<string>;
  loading: boolean;
  error: boolean;
  reload: () => void;
  toggle: (clipId: string) => void;
  setFavourites: (clipIds: string[], favourite: boolean) => Promise<boolean>;
  undoFavourites: (change: FavouriteChange) => Promise<boolean>;
  selection: CLIP[];
  clipEntry: { id: string; revision: number } | null;
  enterClip: (clipId: string) => void;
  workspace: 'timeline' | 'sidebar';
  setWorkspace: React.Dispatch<React.SetStateAction<'timeline' | 'sidebar'>>;
  feedback: StarFeedback | null;
  editor: EditorData;
  setEditor: React.Dispatch<React.SetStateAction<EditorData>>;
  actions: React.MutableRefObject<EditorActions | null>;
  getAngleLockClips: (clip: CLIP) => CLIP[];
  setAngleLocks: (clips: CLIP[], angleId: number | null) => Promise<boolean>;
  lists: Record<SidebarList, SidebarClip[]>;
  sorts: Record<SidebarList, ClipSort>;
  setSort: (list: SidebarList, sort: ClipSort) => void;
  playbackClips: SidebarClip[] | null;
  playbackList: SidebarList | null;
  setPlaybackList: React.Dispatch<React.SetStateAction<SidebarList | null>>;
  playerSeek: React.MutableRefObject<((seconds: number) => void) | null>;
  playerEnded: React.MutableRefObject<((seconds: number) => void) | null>;
};

const Context = createContext<FavouritesContext | null>(null);

const Provider = ({ feedId, children }: { feedId: string; children: React.ReactNode }) => {
  const [ids, setIds] = useState<Set<string>>(new Set());
  const savedFavourites = useRef(new Map<string, ClipFavourite>());
  const [addedAt, setAddedAt] = useState<Record<string, number>>({});
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());
  const [hideSent, setHideSent] = useState(false);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const pendingRef = useRef(new Set<string>());
  const queuedFavourites = useRef(new Map<string, boolean>());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [editor, setEditor] = useState<EditorData>(emptyEditor);
  const actions = useRef<EditorActions | null>(null);
  const pendingAngleLocks = useRef(new Set<string>());
  const currentAngle = useAppSelector((state) => state.sharedStore.currentVideoThatInFrame);
  const playerSeek = useRef<((seconds: number) => void) | null>(null);
  const playerEnded = useRef<((seconds: number) => void) | null>(null);
  const [sorts, setSorts] = useState<Record<SidebarList, ClipSort>>({ selected: 'chronological', favourites: 'chronological' });
  const [playbackList, setPlaybackList] = useState<SidebarList | null>(null);
  const [workspace, setWorkspace] = useState<'timeline' | 'sidebar'>('timeline');
  const [clipEntry, setClipEntry] = useState<{ id: string; revision: number } | null>(null);
  const enterClip = useCallback((id: string) => setClipEntry((previous) => ({ id: String(id), revision: (previous?.revision || 0) + 1 })), []);
  const [feedback, setFeedback] = useState<StarFeedback | null>(null);
  const { pathname } = useLocation();
  const selection: CLIP[] = useAppSelector((state) => pathname.includes('/Timeline')
    ? state.videoStore.sortedSelectedClips : state.composersStore.currentSelectedComposerClips);
  const [selectionOrder, setSelectionOrder] = useState<string[]>([]);

  useEffect(() => {
    const selectedIds = new Set(selection.map((clip) => String(clip.id)));
    setSelectionOrder((previous) => {
      const retained = previous.filter((id) => selectedIds.has(id));
      const previousIds = new Set(previous);
      const added = selection.filter((clip) => !previousIds.has(String(clip.id)))
        .slice().sort((a, b) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0) || (a.clipIndex ?? 0) - (b.clipIndex ?? 0));
      const next = [...retained, ...added.map((clip) => String(clip.id))];
      return next.length === previous.length && next.every((id, index) => id === previous[index]) ? previous : next;
    });
  }, [selection]);

  const { lists, sentCount } = useMemo(() => {
    const selectedIds = new Set(selection.map((clip) => String(clip.id)));
    const selectionRanks = new Map(selectionOrder.map((id, index) => [id, index]));
    const allClips = editor.rows.flatMap((row, rowIndex) => row.clips.map((clip, clipIndex) => ({
      ...clip, topicId: row.id, topicName: row.name, rowIndex, clipIndex,
    })));
    const ordered = (list: SidebarList) => allClips.filter((clip) =>
      (list === 'selected' ? selectedIds : ids).has(String(clip.id))).sort((a, b) => {
      const rowOrder = a.rowIndex - b.rowIndex || a.clipIndex - b.clipIndex;
      if (sorts[list] === 'chronological') return a.time_start - b.time_start || rowOrder;
      if (sorts[list] === 'added') {
        const rank = (id: string) => list === 'selected'
          ? selectionRanks.get(String(id)) ?? Number.MAX_SAFE_INTEGER : addedAt[String(id)] ?? 0;
        return rank(a.id) - rank(b.id) || rowOrder;
      }
      return rowOrder;
    });
    const favourites = ordered('favourites');
    return {
      lists: {
        selected: ordered('selected'),
        favourites: hideSent ? favourites.filter((clip) => !sentIds.has(String(clip.id))) : favourites,
      },
      sentCount: favourites.filter((clip) => sentIds.has(String(clip.id))).length,
    };
  }, [editor.rows, selection, selectionOrder, ids, addedAt, sorts, hideSent, sentIds]);
  const setSort = useCallback((list: SidebarList, sort: ClipSort) => setSorts((previous) => ({ ...previous, [list]: sort })), []);
  const playbackClips = workspace === 'sidebar' && playbackList ? lists[playbackList] : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getClipFavourites(feedId).then(({ data }) => {
      if (!cancelled) {
        savedFavourites.current = new Map(data.map((item) => {
          const favourite = typeof item === 'number' ? { clipId: item, createdAt: null } : item;
          return [String(favourite.clipId), favourite];
        }));
        setIds(new Set(savedFavourites.current.keys()));
        setSentIds(new Set(data.flatMap((item) => typeof item !== 'number' && item.sentToComposer ? [String(item.clipId)] : [])));
        setAddedAt(Object.fromEntries(data.map((item) => typeof item === 'number'
          ? [String(item), 0] : [String(item.clipId), item.createdAt ? Date.parse(item.createdAt) : 0])));
      }
    }).catch(() => {
      if (!cancelled) setError(true);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [feedId, reloadKey]);

  const setFavourites = useCallback(async (
    clipIds: string[], favourite: boolean,
    options?: { recordHistory?: boolean; restore?: ClipFavourite[] },
  ): Promise<boolean> => {
    if (loading || error) return false;
    if (options?.recordHistory === false && pendingRef.current.size) return false;
    const changedIds = Array.from(new Set(clipIds.map(String))).filter((id) => {
      if (pendingRef.current.has(id)) {
        queuedFavourites.current.set(id, favourite);
        return false;
      }
      return savedFavourites.current.has(id) !== favourite;
    });
    if (!changedIds.length) return true;
    const previous = changedIds.flatMap((id) => {
      const saved = savedFavourites.current.get(id);
      return saved ? [saved] : [];
    });
    changedIds.forEach((id) => pendingRef.current.add(id));
    setPending(new Set(pendingRef.current));
    try {
      const { data } = await setClipFavourites(feedId, changedIds, favourite, options?.restore);
      setSentIds((previous) => {
        const next = new Set(previous);
        changedIds.forEach((id) => next.delete(id));
        data.forEach((item) => { if (item.sentToComposer) next.add(String(item.clipId)); });
        return next;
      });
      setAddedAt((previous) => {
        const next = { ...previous };
        changedIds.forEach((id) => { delete next[id]; });
        data.forEach((item) => { next[String(item.clipId)] = item.createdAt ? Date.parse(item.createdAt) : 0; });
        return next;
      });
      changedIds.forEach((id) => savedFavourites.current.delete(id));
      data.forEach((item) => savedFavourites.current.set(String(item.clipId), item));
      setIds(new Set(savedFavourites.current.keys()));
      if (options?.recordHistory !== false) {
        actions.current?.onFavouritesChange?.({ clipIds: changedIds, favourite, previous });
      }
      setFeedback({ clipIds: new Set(changedIds), favourite, key: Date.now() });
      enqueueSnackbar(favourite ? 'Added to favourites' : 'Removed from favourites', { variant: 'success' });
      return true;
    } catch {
      enqueueSnackbar('Could not save favourites. Please try again.', { variant: 'error' });
      return false;
    } finally {
      changedIds.forEach((id) => pendingRef.current.delete(id));
      setPending(new Set(pendingRef.current));
      // Keep the last keypress for each clip while its request is in flight.
      const queuedAdds = changedIds.filter((id) => queuedFavourites.current.get(id) === true);
      const queuedRemoves = changedIds.filter((id) => queuedFavourites.current.get(id) === false);
      changedIds.forEach((id) => queuedFavourites.current.delete(id));
      if (queuedAdds.length) void setFavourites(queuedAdds, true);
      if (queuedRemoves.length) void setFavourites(queuedRemoves, false);
    }
  }, [feedId, loading, error]);
  const undoFavourites = useCallback((change: FavouriteChange) => setFavourites(change.clipIds, !change.favourite, {
    recordHistory: false, restore: change.favourite ? undefined : change.previous,
  }), [setFavourites]);
  const toggle = useCallback((clipId: string) => setFavourites([clipId], !ids.has(String(clipId))), [ids, setFavourites]);

  const getAngleLockClips = useCallback((clip: CLIP) => {
    if (selection.length < 2 || !selection.some((selected) => String(selected.id) === String(clip.id)) ||
      !selection.some((selected) => actions.current?.isPlayheadOverClip(selected))) return [clip];
    const selectedIds = new Set(selection.map((selected) => String(selected.id)));
    return editor.rows.flatMap((row) => row.clips).filter((selected) => selectedIds.has(String(selected.id)));
  }, [selection, editor.rows]);

  const setAngleLocks = useCallback(async (clips: CLIP[], angleId: number | null) => {
    const editorActions = actions.current;
    if (!editorActions || !clips.length || clips.some((clip) => pendingAngleLocks.current.has(String(clip.id)))) return false;
    clips.forEach((clip) => pendingAngleLocks.current.add(String(clip.id)));
    try {
      const results = await Promise.allSettled(clips.map((clip) => editorActions.setAngleLock(clip, angleId)));
      if (results.some((result) => result.status === 'rejected')) throw new Error('Could not save angle locks');
      return true;
    } finally {
      clips.forEach((clip) => pendingAngleLocks.current.delete(String(clip.id)));
    }
  }, []);

  useEffect(() => {
    if (!feedback) return;
    const timeout = window.setTimeout(() => setFeedback(null), 800);
    return () => window.clearTimeout(timeout);
  }, [feedback]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat || event.isComposing || event.metaKey) return;
      if ((event.ctrlKey || event.altKey) && !event.getModifierState('AltGraph')) return;
      const favourite = event.key === '+' || event.key === '=' ? true
        : event.key === '-' || event.key === '−' ? false
        : event.code === 'Equal' || event.code === 'NumpadAdd' ? true
        : event.code === 'Minus' || event.code === 'NumpadSubtract' ? false : null;
      const lockAngle = !event.ctrlKey && !event.altKey && !event.shiftKey &&
        (event.code === 'KeyL' || event.key.toLowerCase() === 'l');
      if (favourite === null && !lockAngle) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target instanceof HTMLElement && target.isContentEditable) return;
      if (target?.closest('input, textarea, select, [role="textbox"], [role="combobox"], [role="dialog"], [role="menu"], [role="listbox"], [data-clip-filter-panel]')) return;
      const blockingOverlay = Array.from(document.querySelectorAll('[role="dialog"], [role="menu"], [role="listbox"]'))
        .some((element) => !element.closest('[aria-hidden="true"]') && element.getClientRects().length > 0 &&
          window.getComputedStyle(element).visibility !== 'hidden');
      if (blockingOverlay) return;
      if (!editor.activeClipId) return;
      if (lockAngle) {
        if (!selection.length) return;
        const clip = editor.rows.flatMap((row) => row.clips)
          .find((item) => String(item.id) === String(editor.activeClipId));
        if (!clip || !editor.canEdit || !actions.current) return;
        const clips = getAngleLockClips(clip);
        const angleLocked = clips.length > 1
          ? clips.every((selected) => selected.locked_angle_id != null && String(selected.locked_angle_id) === String(currentAngle?.id))
          : clip.locked_angle_id != null;
        if (!angleLocked && (!currentAngle || !clips.every(actions.current.canLockAngle))) return;
        event.preventDefault();
        void setAngleLocks(clips, angleLocked ? null : Number(currentAngle!.id))
          .then((saved) => {
            if (saved) enqueueSnackbar(clips.length > 1
              ? `Angle ${angleLocked ? 'unlocked' : 'locked'} for ${clips.length} selected clips`
              : angleLocked ? 'Angle unlocked' : 'Angle locked for this clip', { variant: 'success' });
          })
          .catch(() => enqueueSnackbar('Could not save angle locks. Please try again.', { variant: 'error' }));
        return;
      }
      event.preventDefault();
      if (favourite !== null) setFavourites([String(editor.activeClipId)], favourite);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [editor.activeClipId, editor.canEdit, editor.rows, currentAngle, setFavourites, selection.length, getAngleLockClips, setAngleLocks]);

  const value = useMemo(() => ({
    feedId, ids, pending, loading, error, toggle, setFavourites, undoFavourites, selection, clipEntry, enterClip, workspace, setWorkspace, feedback, editor, setEditor, actions, getAngleLockClips, setAngleLocks,
    lists, sorts, setSort, hideSent, setHideSent, sentCount, playbackClips, playbackList, setPlaybackList, playerSeek, playerEnded,
    reload: () => setReloadKey((key) => key + 1),
  }), [feedId, ids, pending, loading, error, toggle, setFavourites, undoFavourites, selection, clipEntry, enterClip, workspace, feedback, editor, getAngleLockClips, setAngleLocks, lists, sorts, setSort, hideSent, sentCount, playbackClips, playbackList]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const ClipFavouritesProvider = ({ children }: { children: React.ReactNode }) => {
  const { search } = useLocation();
  const feedId = new URLSearchParams(search).get('feedId') || '';
  const user = useAppSelector((state) => state.user.currentUser);
  return <Provider key={`${user?.id}:${user?.organization?.id}:${feedId}`} feedId={feedId}>{children}</Provider>;
};

export const useClipFavourites = () => {
  const context = useContext(Context);
  if (!context) throw new Error('Clip favourites require a video page');
  return context;
};

export const useSidebarPlayerSeek = (seek: (seconds: number) => void) => {
  const { playerSeek, playerEnded, playbackClips } = useClipFavourites();
  useLayoutEffect(() => {
    playerSeek.current = seek;
    return () => { playerSeek.current = null; };
  });
  return { active: playbackClips !== null, onEnded: (seconds: number) => playerEnded.current?.(seconds) };
};

// Keep callbacks current without rerendering the sidebar on every playback tick.
export const useFavouriteEditor = (data: EditorData & EditorActions) => {
  const { setEditor, actions, setWorkspace } = useClipFavourites();
  useEffect(() => {
    setEditor({ rows: data.rows, canEdit: data.canEdit, canSend: data.canSend, activeClipId: data.activeClipId });
    return () => setEditor(emptyEditor);
  }, [setEditor, data.rows, data.canEdit, data.canSend, data.activeClipId]);
  useLayoutEffect(() => {
    actions.current = { edit: data.edit, setAngleLock: data.setAngleLock, canLockAngle: data.canLockAngle, isPlayheadOverClip: data.isPlayheadOverClip, seek: data.seek, send: data.send, pause: data.pause, select: data.select, deselect: data.deselect, onFavouritesChange: data.onFavouritesChange };
    return () => { actions.current = null; };
  });
  return useCallback((event: React.PointerEvent) => {
    if ((event.target as Element).closest('[data-preserve-clip-workspace]')) return;
    setWorkspace('timeline');
  }, [setWorkspace]);
};
